import { Link, useNavigate } from 'react-router-dom'
import { Alert, Anchor, Button, Code, Group, List, Stack, Table, Text, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconCalendarEvent, IconLock, IconSearch } from '@tabler/icons-react'
import {
  Breadcrumbs, DetailShell, EmptyState, MetaList, PanelGrid, SectionCard, Skeleton, StatusBadge,
} from '@lighthouse/design'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import { useExecution } from '../../hooks/useExecutions'
import { DETAIL_ADMIN_ONLY, NOT_MEASURED, exactNumber, humanizePhase, whenLabel } from '../../utils/format'
import { errorTextView, haltDetailView } from '../../utils/haltDetail'
import type { ExecutionDetail as ExecutionDetailData, ExecutionEntity } from '../../types/executions'
import {
  HALT_NOT_CLEARED, countsBasisText, detailView, durationText, entityCountSummary, entityDurationText,
  entityOutcomePresentation, entityRowsTrimmedText, haltClearedText, hasAnomaly, notFoundText, recordsLoadedCaveatFor,
  runOutcomePresentation, schedulerLink, schedulerLinkRefusal, schedulerLinkUnavailable, staleRuleText,
  targetText, windowText,
} from './executionFormat'
import { ExecutionErrorPanel, HangfireJob, OutcomeBadge, RecordsLoaded } from './ExecutionList'
import { haltConsequenceText } from '../jobs/requestFormat'
import { CancelRunAction } from '../cancel/CancelRunAction'
import { CancellationCard } from '../cancel/CancellationCard'
import { isCancellableOutcome, runScopeText } from '../cancel/cancelFormat'
import type { EtlJobCatalog } from '../../types/etlJobs'

/**
 * Execution History — one run in full: its header, its halt, its last error,
 * what in its record does not describe the run as created, and its entity rows,
 * each linked into the ETL Explorer (the entity grain).
 *
 * Polls while the run is live (useExecution); a finished run does not change.
 *
 * The halt's detail and every error on a halted run are administrator-only (D8): the server withholds them and says
 * so with a `…Withheld` flag, and this page renders DETAIL_ADMIN_ONLY, dimmed, in their place — never a blank or the
 * unmeasured dash. The flag decides, never the caller's role.
 *
 * CANCELLING (D6b(ii)): a running or stale run offers "Cancel run ETL-…" in its header (components/cancel), and a
 * running, stale or cancelled run shows its cancel request — status, who and when, the Hangfire jobs the stop went
 * to, and what became of the run. Both are mounted only for those outcomes, so a finished run reads nothing more.
 */
export function ExecutionDetail({ runId }: { runId: string }) {
  const { data, isLoading, isError, error } = useExecution(runId)
  const catalog = useEtlJobCatalog()
  const view = detailView({ isError, error, isLoading, data })

  if (view === 'not-found') {
    return (
      <Stack gap="md" pb="xl">
        <DetailBreadcrumbs current="Not found" />
        <EmptyState
          icon={<IconSearch size={28} stroke={1.5} />}
          title={notFoundText(runId)}
          action={<Button component={Link} to="/execution-history" variant="light">Back to Execution History</Button>}
        />
      </Stack>
    )
  }

  if (view === 'error') {
    return (
      <Stack gap="md" pb="xl">
        <DetailBreadcrumbs current={runId} />
        <ExecutionErrorPanel error={error} />
      </Stack>
    )
  }

  // `|| !data` is load-bearing: between retries a failed query has no error and no data.
  if (isLoading || !data) {
    return (
      <Stack gap="md" aria-busy>
        <Skeleton height={150} />
        <Skeleton height={220} />
      </Stack>
    )
  }

  const presentation = runOutcomePresentation(data.outcome)
  const rerun = schedulerLink(data.requested, catalog.data?.jobs)
  // Undefined jobs (loading, or the catalogue failed) says "has not loaded" — never "no such job".
  const rerunUnavailable = schedulerLinkUnavailable(data.requested, catalog.data?.jobs)
  // The server has said this caller may not run jobs: the button stays, dimmed, with its reason.
  const rerunRefused = schedulerLinkRefusal(catalog.data)
  // The served caveat, or (for a halted or stale run counted from rows that may be trimmed) ours.
  const recordsCaveat = recordsLoadedCaveatFor(data)
  // A run that can still be stopped; and one whose cancel request is the story (a cancelled run's included).
  const cancellable = isCancellableOutcome(data.outcome)
  const showCancellation = cancellable || data.outcome === 'cancelled'

  const schedulerAction = rerun && rerunRefused ? (
    // Disabled rather than hidden, as the Scheduler's Run and the Explorer's Rerun are. data-disabled
    // rather than disabled, so the tooltip with the server's reason still shows.
    <Tooltip label={rerunRefused} multiline w={280}>
      <Button
        size="compact-sm"
        variant="default"
        leftSection={<IconLock size={14} />}
        data-disabled
        aria-disabled
        aria-label={`Open in Scheduler. ${rerunRefused}`}
        onClick={(event) => event.preventDefault()}
      >
        Open in Scheduler
      </Button>
    </Tooltip>
  ) : rerun ? (
    <Tooltip label="Opens the Scheduler with this run's target and window filled in. Nothing runs until you press Run there." multiline w={280}>
      <Button component={Link} to={rerun} size="compact-sm" variant="default" leftSection={<IconCalendarEvent size={14} />}>
        Open in Scheduler
      </Button>
    </Tooltip>
  ) : (
    <Text size="xs" c="dimmed">
      {catalog.isLoading ? 'Loading the job catalogue…' : rerunUnavailable}
    </Text>
  )

  return (
    <Stack gap="md" pb="xl">
      <DetailShell
        breadcrumbs={<DetailBreadcrumbs current={data.runKey} />}
        title={data.runKey}
        badges={
          <Group gap={6}>
            <OutcomeBadge presentation={presentation} />
            {data.halted && data.outcome !== 'halted' && <StatusBadge status="critical" label="Halted" />}
          </Group>
        }
        eyebrow={data.orchestratorName}
        actions={
          cancellable ? (
            <Group gap="xs" wrap="wrap">
              {schedulerAction}
              <CancelRunAction
                runId={data.runId}
                runKey={data.runKey}
                runOutcome={data.outcome}
                scope={runScopeText(data.runKey, data, false)}
              />
            </Group>
          ) : (
            schedulerAction
          )
        }
      >
        <Stack gap="lg">
          <MetaList
            items={[
              { label: 'Status', value: <OutcomeBadge presentation={presentation} /> },
              { label: 'Stored status', value: data.status, mono: true },
              { label: 'Orchestrator', value: data.orchestratorName, mono: true },
              { label: 'Target', value: targetText(data.requested) },
              {
                label: 'Window',
                value: (
                  <Stack gap={2}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{windowText(data.requested)}</span>
                    {data.requested && <Text size="xs" c="dimmed">{data.requested.basis} The end date is exclusive.</Text>}
                  </Stack>
                ),
              },
              { label: 'Started', value: whenLabel(data.startedAt) },
              { label: 'Ended', value: whenLabel(data.completedAt) },
              { label: 'Duration', value: durationText(data) },
              { label: 'Last heartbeat', value: whenLabel(data.lastHeartbeatAt) },
              { label: 'Worker', value: data.workerName ?? NOT_MEASURED, mono: true },
              { label: 'Environment', value: data.environmentName ?? NOT_MEASURED },
              { label: 'Trigger', value: humanizePhase(data.trigger) },
              { label: 'Mode', value: humanizePhase(data.mode) },
              { label: 'Was reset (as recorded)', value: data.wasReset ? 'Yes' : 'No' },
              { label: 'Hangfire job', value: <HangfireJob id={data.hangfireJobId} /> },
              { label: 'Run id', value: data.runId, mono: true },
            ]}
          />
          {(data.outcome === 'stale' || data.entitiesStale > 0) && (
            <Text size="xs" c="dimmed">{staleRuleText(data.staleRule)}.</Text>
          )}
        </Stack>
      </DetailShell>

      {data.halted && <HaltCard run={data} catalog={catalog.data} />}

      {showCancellation && <CancellationCard runId={data.runId} live={cancellable} />}

      <LastErrorCard view={errorTextView(data.lastError, data.lastErrorWithheld)} />

      {hasAnomaly(data.anomalies) && <AnomaliesCard run={data} />}

      <PanelGrid>
        <SectionCard title="Entities" subtitle={entityCountSummary(data)}>
          <MetaList
            items={[
              { label: 'Expected at creation', value: exactNumber(data.totalEntitiesRecordedAtCreation) },
              { label: 'Succeeded', value: exactNumber(data.entitiesSucceeded) },
              { label: 'Failed', value: exactNumber(data.entitiesFailed) },
              { label: 'Skipped', value: exactNumber(data.entitiesSkipped) },
              { label: 'Running', value: exactNumber(data.entitiesRunning) },
              { label: 'Stale', value: exactNumber(data.entitiesStale) },
              { label: 'Not started', value: exactNumber(data.entitiesPending) },
              { label: 'Cancelled (not a failure)', value: exactNumber(data.entitiesCancelled ?? 0) },
              { label: 'Counted from', value: countsBasisText(data.countsBasis) },
              { label: 'Entity rows on file', value: exactNumber(data.entityRowCount) },
              { label: 'Rows complete', value: entityRowsTrimmedText(data.entityRowsTrimmed) },
            ]}
          />
        </SectionCard>
        <SectionCard title="Records">
          <MetaList
            items={[
              { label: 'Records loaded', value: <RecordsLoaded value={data.recordsLoaded} caveat={recordsCaveat} /> },
            ]}
          />
          {recordsCaveat && <Text size="xs" c="dimmed" mt="xs">{recordsCaveat}</Text>}
        </SectionCard>
      </PanelGrid>

      <EntitiesTable run={data} />
    </Stack>
  )
}

function DetailBreadcrumbs({ current }: { current: string }) {
  const navigate = useNavigate()
  return (
    <Breadcrumbs
      items={[
        { label: 'Operations' },
        { label: 'Execution History', onClick: () => navigate('/execution-history') },
        { label: current },
      ]}
    />
  )
}

/** Recorded text this caller may not read: dimmed, never blank and never the unmeasured dash. */
function Withheld({ size = 'sm' }: { size?: 'xs' | 'sm' }) {
  return <Text size={size} c="dimmed" data-state="detail-withheld">{DETAIL_ADMIN_ONLY}</Text>
}

/** The run's last error: the text, a withheld notice, or nothing at all when none was recorded. */
function LastErrorCard({ view }: { view: ReturnType<typeof errorTextView> }) {
  if (view.kind === 'none') return null
  return (
    <SectionCard title="Last error" subtitle="As the run recorded it">
      {view.kind === 'withheld' ? (
        <Withheld />
      ) : (
        <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 360, overflowY: 'auto' }}>
          {view.text}
        </Code>
      )}
    </SectionCard>
  )
}

/**
 * The halt. While uncleared it blocks every request the server does not exempt
 * — so it says so, in the Scheduler's own sentence built from the served exempt
 * categories, and where it is shown. Clearing it is offered from the Scheduler's
 * halt panel, which this card links to; the card itself is read-only.
 */
function HaltCard({ run, catalog }: { run: ExecutionDetailData; catalog: EtlJobCatalog | undefined }) {
  const detail = haltDetailView(run.haltDetail, run.haltDetailWithheld)
  const recorded = detail.kind === 'recorded' ? detail.diagnosis : null
  const clearNote = detail.kind === 'recorded' ? detail.note : null
  return (
    <SectionCard title="Halt" subtitle={humanizePhase(run.haltReason)}>
      <Stack gap="sm">
        <MetaList
          items={[
            { label: 'Reason', value: humanizePhase(run.haltReason) },
            { label: 'Halted', value: whenLabel(run.haltedAt) },
            { label: 'Detail', value: detail.kind === 'withheld' ? <Withheld /> : recorded ?? NOT_MEASURED },
          ]}
        />
        {run.haltAcknowledged ? (
          <Alert color="gray" variant="light" title={haltClearedText(run.haltAcknowledgedAt, run.haltAcknowledgedBy)}>
            {detail.kind === 'withheld' ? (
              <Withheld />
            ) : (
              <Text size="sm">
                {clearNote ? <>Note left when clearing: {clearNote}</> : 'No note was left when clearing.'}
              </Text>
            )}
          </Alert>
        ) : (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title={HALT_NOT_CLEARED}>
            <Text size="sm">
              {haltConsequenceText(catalog)}{' '}
              <Anchor component={Link} to="/jobs" size="sm">See it in the Scheduler</Anchor>
            </Text>
          </Alert>
        )}
      </Stack>
    </SectionCard>
  )
}

/** What in the run's record does not describe the run as it was created, with the served explanation. */
function AnomaliesCard({ run }: { run: ExecutionDetailData }) {
  const { entitiesOutsideRecordedTarget, entityRowsExceedTotal, resumedEntityCount } = run.anomalies
  return (
    <SectionCard title="This run holds more than it was created for">
      <Stack gap="sm">
        <List size="sm" spacing={4}>
          {entitiesOutsideRecordedTarget.length > 0 && (
            <List.Item>
              Entities outside the recorded target ({targetText(run.requested)}):{' '}
              {entitiesOutsideRecordedTarget.join(', ')}
            </List.Item>
          )}
          {entityRowsExceedTotal && (
            <List.Item>
              {exactNumber(run.entityRowCount)} entity rows, more than the{' '}
              {exactNumber(run.totalEntitiesRecordedAtCreation)} the run was created expecting
            </List.Item>
          )}
          <List.Item>
            Entity rows resumed (attempted more than once):{' '}
            {resumedEntityCount == null ? `${NOT_MEASURED} (some rows are past retention, so a count would be a floor)` : exactNumber(resumedEntityCount)}
          </List.Item>
        </List>
        <Text size="sm" c="dimmed">{run.anomalyExplanation}</Text>
      </Stack>
    </SectionCard>
  )
}

/** The entity rows, each opening its Explorer run. Capped server-side; the cap is said, not hidden. */
function EntitiesTable({ run }: { run: ExecutionDetailData }) {
  const navigate = useNavigate()
  const capped = run.entities.length < run.entityRowCount
  const subtitle = capped
    ? `Showing ${exactNumber(run.entities.length)} of ${exactNumber(run.entityRowCount)}, by level then name`
    : 'By level then name'

  return (
    <SectionCard title="Entity runs" subtitle={subtitle} flush>
      {run.entities.length === 0 ? (
        <EmptyState
          title="No entity rows on file"
          message={run.entityRowsTrimmed
            ? 'Every finished row of this run is past retention; the counts above come from its rollup.'
            : 'The run recorded no entity rows — it may have stopped before enrolling any.'}
        />
      ) : (
        <Table.ScrollContainer minWidth={980}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Entity</Table.Th>
                <Table.Th>Outcome</Table.Th>
                <Table.Th ta="right">Level</Table.Th>
                <Table.Th>Started</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th ta="right">Attempts</Table.Th>
                <Table.Th ta="right">Records</Table.Th>
                <Table.Th>Error</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {run.entities.map((entity) => (
                <EntityRow
                  key={entity.entityRunId}
                  entity={entity}
                  onOpen={() => navigate(`/etl-explorer/${entity.entityRunId}`)}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </SectionCard>
  )
}

function EntityRow({ entity, onOpen }: { entity: ExecutionEntity; onOpen: () => void }) {
  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={onOpen}>
      <Table.Td>
        <Anchor
          component={Link}
          to={`/etl-explorer/${entity.entityRunId}`}
          size="sm"
          onClick={(event) => event.stopPropagation()}
        >
          {entity.entityName}
        </Anchor>
        <Text size="xs" c="dimmed">{entity.module ?? 'No module recorded'}</Text>
      </Table.Td>
      <Table.Td><OutcomeBadge presentation={entityOutcomePresentation(entity.outcome)} /></Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{entity.levelNumber}</Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>{whenLabel(entity.startedAt)}</Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{entityDurationText(entity)}</Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{exactNumber(entity.attemptCount)}</Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        <RecordsLoaded value={entity.recordsLoaded} caveat={entity.recordsLoadedCaveat} />
        {entity.recordsRejected > 0 && (
          <Text size="xs" c="orange">{exactNumber(entity.recordsRejected)} rejected</Text>
        )}
      </Table.Td>
      <Table.Td style={{ maxWidth: 360 }}>
        <EntityError entity={entity} />
      </Table.Td>
    </Table.Tr>
  )
}

/** One entity's error: its first line, a withheld notice, or the unmeasured dash when none was recorded. */
function EntityError({ entity }: { entity: ExecutionEntity }) {
  const view = errorTextView(entity.lastErrorFirstLine, entity.lastErrorWithheld)
  if (view.kind === 'withheld') return <Withheld size="xs" />
  if (view.kind === 'none') return <>{NOT_MEASURED}</>
  return <Text size="xs" c="red" lineClamp={2} title={view.text}>{view.text}</Text>
}
